import React, { useMemo, useState } from 'react';
import { useLanguage, Language } from '../contexts/LanguageContext';
import { FormFinder, FORM_NAME } from './GuiaPage';

// Página a la que Stripe devuelve al cliente después de pagar
// (?plan=solo | pareja | familia). Primero el asistente "¿qué formulario
// necesito?" (FormFinder, el mismo de /guia); al terminar abre directo el
// cuestionario correcto en el idioma elegido. Debajo, lista plegable con los 7
// formularios por si el cliente ya sabe cuál es. Los IDs son los formularios V3
// (2026-10-11). El sitio usa 'ht' para kreyòl; los formularios usan 'KR'.
// EN/ES finalizados. HT/VI = mejor esfuerzo, pendiente de revisión nativa.
const BASE = 'https://forms.fillout.com/t/';
const CONTACT_EMAIL = 'info@noorpassportservices.com';
type FormLang = 'EN' | 'ES' | 'VI' | 'KR';
const FORMS: {
  code: string;
  ids: Record<FormLang, string>;
}[] = [{
  code: 'DS-11',
  ids: {
    ES: 'vKMU6u8Uttus',
    EN: 'iTRp2y4xpDus',
    VI: '4EuFAQnAJkus',
    KR: 'qCHH7BVEhcus'
  }
}, {
  code: 'DS-82',
  ids: {
    ES: 'feZ6sJcvHQus',
    EN: '3QyUMfUk4pus',
    VI: 'n9ycu1BP5zus',
    KR: '8uPrB11Krrus'
  }
}, {
  code: 'DS-5504',
  ids: {
    ES: '8Wfjf3D9RHus',
    EN: 'naMQFQLKdEus',
    VI: 'cVED25NPGLus',
    KR: 'rFJxGvrZV1us'
  }
}, {
  code: 'DS-3053',
  ids: {
    ES: 'sW7HwRC5mNus',
    EN: '1rrf87DHPFus',
    VI: '47bXbSJRR8us',
    KR: 'htUfoGoZpGus'
  }
}, {
  code: 'DS-5525',
  ids: {
    ES: '6LGrUVYLZDus',
    EN: 'xxQukjj7bRus',
    VI: 'p5vnEdBBh1us',
    KR: '1kJWRHKtLnus'
  }
}, {
  code: 'DS-64',
  ids: {
    ES: 'tLuzWywqxzus',
    EN: 'fh7Ednj75uus',
    VI: '6H1jh8M7Wgus',
    KR: '5qciWVFUMLus'
  }
}, {
  code: 'DS-86',
  ids: {
    ES: 'fhJFXcE4hAus',
    EN: 'oxXr4GTzeEus',
    VI: 'eoc2XdZvWxus',
    KR: 'iBxgwQswDfus'
  }
}];
const FORM_LANG: Record<Language, FormLang> = {
  en: 'EN',
  es: 'ES',
  ht: 'KR',
  vi: 'VI'
};
type Strings = {
  title: string;
  intro: string;
  guideTitle: string;
  guideBody: string;
  openFor: string;
  fillEach: string;
  unsure: string;
  allTitle: string;
  allBody: string;
  multi: string;
  open: string;
  note: string;
  mailNote: string;
  disclaimer: string;
};
const T: Record<Language, Strings> = {
  es: {
    title: '¡Gracias! Su pago fue recibido',
    intro: 'El siguiente paso es llenar su cuestionario. Le toma unos minutos y está en su idioma.',
    guideTitle: '¿Qué formulario necesito?',
    guideBody: 'Responda unas preguntas sencillas y le abrimos el cuestionario que le corresponde.',
    openFor: 'Abrir cuestionario',
    fillEach: 'Llene uno por cada formulario indicado.',
    unsure: 'Si el resultado no le queda claro, escríbanos a',
    allTitle: 'Ya sé cuál es mi formulario',
    allBody: 'Elija directamente el cuestionario:',
    multi: 'Su paquete incluye a más de una persona: llene un cuestionario por cada persona.',
    open: 'Abrir cuestionario',
    note: 'Usted no firma nada aquí. Nosotros preparamos su formulario; usted lo firma únicamente frente al agente autorizado, cuando se lo pida.',
    mailNote: 'También le enviamos este enlace por correo.',
    disclaimer: 'Esta guía es una orientación general, no asesoría legal. Revisaremos su cuestionario y, si el formulario no fuera el correcto, nos comunicaremos con usted.'
  },
  en: {
    title: 'Thank you! Your payment was received',
    intro: 'The next step is to fill out your questionnaire. It takes a few minutes and is in your language.',
    guideTitle: 'Which form do I need?',
    guideBody: 'Answer a few simple questions and we will open the questionnaire that fits you.',
    openFor: 'Open questionnaire',
    fillEach: 'Fill out one for each form listed.',
    unsure: 'If the result is not clear, write to us at',
    allTitle: 'I already know my form',
    allBody: 'Choose the questionnaire directly:',
    multi: 'Your package covers more than one person: fill out one questionnaire for each person.',
    open: 'Open questionnaire',
    note: 'You do not sign anything here. We prepare your form; you sign it only in front of the authorized agent, when asked.',
    mailNote: 'We also emailed you this link.',
    disclaimer: 'This guide is general orientation, not legal advice. We will review your questionnaire and, if the form turns out not to be the right one, we will contact you.'
  },
  ht: {
    title: 'Mèsi! Nou resevwa peman ou',
    intro: 'Pwochen etap la se ranpli kesyonè ou a. Li pran kèk minit epi li nan lang ou.',
    guideTitle: 'Ki fòm mwen bezwen?',
    guideBody: 'Reponn kèk kesyon senp epi nou ap louvri kesyonè ki bon pou ou a.',
    openFor: 'Louvri kesyonè a',
    fillEach: 'Ranpli youn pou chak fòm ki endike.',
    unsure: 'Si rezilta a pa klar, ekri nou nan',
    allTitle: 'Mwen deja konnen ki fòm mwen an',
    allBody: 'Chwazi kesyonè a dirèkteman:',
    multi: 'Pakè ou a gen plis pase yon moun: ranpli yon kesyonè pou chak moun.',
    open: 'Louvri kesyonè a',
    note: 'Ou pa siyen anyen isit la. Nou prepare fòm ou a; ou siyen l sèlman devan ajan otorize a, lè yo mande w.',
    mailNote: 'Nou voye lyen sa a ban ou pa imèl tou.',
    disclaimer: 'Gid sa a se yon oryantasyon jeneral, pa konsèy legal. Nou ap revize kesyonè ou a epi, si fòm nan pa t bon, nou ap kontakte ou.'
  },
  vi: {
    title: 'Cảm ơn quý vị! Chúng tôi đã nhận được thanh toán',
    intro: 'Bước tiếp theo là điền bảng câu hỏi. Chỉ mất vài phút và bằng ngôn ngữ của quý vị.',
    guideTitle: 'Tôi cần mẫu đơn nào?',
    guideBody: 'Trả lời vài câu hỏi đơn giản và chúng tôi sẽ mở bảng câu hỏi phù hợp.',
    openFor: 'Mở bảng câu hỏi',
    fillEach: 'Điền một bảng cho mỗi mẫu đơn được nêu.',
    unsure: 'Nếu kết quả chưa rõ, hãy viết cho chúng tôi tại',
    allTitle: 'Tôi đã biết mẫu đơn của mình',
    allBody: 'Chọn trực tiếp bảng câu hỏi:',
    multi: 'Gói của quý vị gồm nhiều người: hãy điền một bảng câu hỏi cho mỗi người.',
    open: 'Mở bảng câu hỏi',
    note: 'Quý vị không ký gì ở đây. Chúng tôi chuẩn bị đơn; quý vị chỉ ký trước mặt nhân viên được ủy quyền khi được yêu cầu.',
    mailNote: 'Chúng tôi cũng đã gửi liên kết này qua email.',
    disclaimer: 'Hướng dẫn này chỉ mang tính tham khảo chung, không phải tư vấn pháp lý. Chúng tôi sẽ xem lại bảng câu hỏi của quý vị và nếu mẫu đơn chưa đúng, chúng tôi sẽ liên hệ.'
  }
};
export function GraciasPage() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  const [plan] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('plan') || '';
    } catch {
      return '';
    }
  });
  const multi = plan === 'pareja' || plan === 'familia';
  const fl = FORM_LANG[language];
  const hrefFor = useMemo(() => {
    const m: Record<string, string> = {};
    FORMS.forEach(f => {
      m[f.code] = BASE + f.ids[fl];
    });
    return m;
  }, [fl]);
  const btn = 'block w-full text-center px-4 py-4 rounded-xl bg-brand-textPrimary text-white font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2';
  const renderActions = (forms: string[]) => <div data-id="element-494" data-mp="3lu737fh36csm">
      {forms.length > 0 ? <>
          <div className="flex flex-col gap-3" data-id="element-495" data-mp="3qvmnv6fcqhah">
            {forms.map(code => <a key={code} href={hrefFor[code]} className={btn} aria-label={`${code} — ${t.openFor}`} data-id="element-496" data-mp="1f7hwvxx9de4n">
                {t.openFor} — {code}
              </a>)}
          </div>
          {forms.length > 1 && <p className="text-sm text-brand-textSecondary mt-3" data-id="element-497" data-mp="1oy2p93mc9mf4">{t.fillEach}</p>}
        </> : <p className="text-brand-textSecondary" data-id="element-498" data-mp="1k6ukikvyep8a">
          {t.unsure}{' '}
          <a className="underline font-medium text-brand-textPrimary" href={`mailto:${CONTACT_EMAIL}`} data-id="element-499" data-mp="1xy91yna7qid8">{CONTACT_EMAIL}</a>
        </p>}
    </div>;
  return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-id="element-500" data-mp="1t1in23a59ste">
      <h1 className="text-3xl sm:text-4xl font-bold text-brand-textPrimary mb-4" data-id="element-501" data-mp="27vdquuvg0rxq">{t.title}</h1>
      <p className="text-lg text-brand-textSecondary mb-8" data-id="element-502" data-mp="22yn81zs97i8m">{t.intro}</p>
      {multi && <p className="font-medium text-brand-textPrimary mb-4" data-id="element-503" data-mp="dhvzewrx3axf">{t.multi}</p>}

      <h2 className="text-xl font-semibold text-brand-textPrimary mb-2" data-id="element-504" data-mp="8jksatt0beiw">{t.guideTitle}</h2>
      <p className="text-brand-textSecondary mb-4" data-id="element-505" data-mp="3ivt2p3pzz73g">{t.guideBody}</p>
      <div className="mb-8" data-id="element-506" data-mp="3iv9tcbeexr11">
        <FormFinder renderActions={renderActions} disclaimerOverride={{
        es: T.es.disclaimer,
        en: T.en.disclaimer,
        ht: T.ht.disclaimer,
        vi: T.vi.disclaimer
      }} data-id="element-507" data-mp="3nuw9aslmlldj" />
      </div>

      <details className="mb-8 rounded-xl border border-brand-border bg-white p-4" data-id="element-508" data-mp="1fbj2p7t9foa">
        <summary className="cursor-pointer font-semibold text-brand-textPrimary" data-id="element-509" data-mp="1he4evy0qkmo">{t.allTitle}</summary>
        <p className="text-brand-textSecondary mt-3 mb-3" data-id="element-510" data-mp="2zza0kydm2jge">{t.allBody}</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-511" data-mp="34wjx0xzf3vdq">
          {FORMS.map(f => <li key={f.code} data-id="element-512" data-mp="39tjtqiadnls6">
              <a href={hrefFor[f.code]} className={btn} aria-label={`${f.code} — ${t.open}`} data-id="element-513" data-mp="39xhe178oyjdn">
                <span className="block" data-id="element-514" data-mp="3dudgv9fzwwk0">{f.code}</span>
                <span className="block text-xs font-normal opacity-80" data-id="element-515" data-mp="p3uhmd126e36">{FORM_NAME[f.code][language]}</span>
              </a>
            </li>)}
        </ul>
      </details>

      <p className="text-sm text-brand-textSecondary mb-2" data-id="element-516" data-mp="2dp1kjznty6ae">{t.note}</p>
      <p className="text-sm text-brand-textSecondary" data-id="element-517" data-mp="2dqcsmbob607b">{t.mailNote}</p>
    </div>;
}